
import { defineComponent, reactive } from 'vue';
import BreadCrumbs from './BreadCrumbs.vue';
import { ClientConstants, Duration, ScreenerCardStyle } from './../client.constants';
import FractionalCdSlider from './../components/FractionalCdSlider.vue';
import store from './../store/store';
import ScreenerCard from './../components/ScreenerCard.vue';
import FdicModal from './FdicModal.vue';
import ScreenerModal from './ScreenerModal.vue';
import { CDDetail } from './../models/fractional-cd-model';
import { Utils } from './../utils/Utils';
import { ContentComponentsModel, ContentDisclosuresModel } from './../models/content-model';

declare let app: any;

interface CardInfo {
  style: ScreenerCardStyle;
  cusip?: string;
  duration?: Duration;
  apy?: string;
  returnAmount?: number;
  title?: string;
  maturityDate?: string;
  searchURL?: string;
}

export default defineComponent({
  name: 'GuidedScreener',
  components: { BreadCrumbs, FractionalCdSlider, ScreenerCard, FdicModal, ScreenerModal },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.disclosures = this.contentDetailList[5]?.disclosures?.slice(3) ?? [];
    try {
      Utils.trackingFunction(this.clientConstants.SEARCH_PAGE);
    } catch (err) {
      console.log('Error In track analytics ' + err);
    }
  },
  unmounted() {
    window.removeEventListener('resize', this.onResize);
  },
  computed: {
    isDesktopWidth() {
      return this.windowWidth > ClientConstants.TABLET_MAX;
    },
    offerings() {
      return this.store.offeringsModel.offerings;
    },
    formattedAmount() {
      return this.investmentAmount.toFixed(0);
    },
    timestampMessage() {
      const timestamp = Utils.formatTimestamp(store.offeringsModel.timestamp);
      return timestamp == '' ? '' : this.clientConstants.GS_TIMESTAMP_MESSAGE.replace('{{ timestamp }}', timestamp);
    },
    currentDuration() {
      return Object.values(Duration)[this.durationIndex];
    },
    cardIndex() {
      return this.offerings.findIndex(off => off.termToMaturity == this.currentDuration);
    },

    // offerings for duration
    fractionalCD() {
      return this.offerings[this.cardIndex].fractionalCD;
    },
    wholeCD() {
      return this.offerings[this.cardIndex].wholeCD;
    },

    // possible cards
    topCard() {
      return this.cardInfo(this.cardIndex, Number(this.investmentAmount));
    },
    mixedWholeCard() {
      return this.cardInfo(this.cardIndex, this.wholePortion);
    },
    mixedFractionalCard() {
      return this.cardInfo(this.cardIndex, this.fractionalPortion);
    },

    // booleans
    isFractionalInvestmentAmount() {
      return this.investmentAmount < 1000;
    },
    isWholeInvestmentAmount() {
      return this.investmentAmount > 0 && this.investmentAmount % 1000 == 0;
    },
    isMixedInvestmentAmount() {
      return this.investmentAmount > 1000 && this.investmentAmount % 1000 != 0;
    },
    isFractionalCDAvailable() {
      return this.fractionalCD != undefined;
    },
    isWholeCDAvailable() {
      return this.wholeCD != undefined;
    },
    isWholeRateBetter() {
      const wholeRate = this.wholeCD?.rate ?? 0;
      const fractionalRate = this.fractionalCD?.rate ?? 0;
      return wholeRate > fractionalRate;
    },
    isMixedOpportunity() {
      return this.isMixedInvestmentAmount && this.isFractionalCDAvailable && this.isWholeRateBetter;
    },
    isOptionAvailable() {
      const needFractionalCD = this.isFractionalInvestmentAmount || this.isMixedInvestmentAmount;
      return (needFractionalCD && this.isFractionalCDAvailable) || (!needFractionalCD && this.isWholeCDAvailable);
    },
    isIncreaseOpportunity() {
      return (this.isFractionalInvestmentAmount || this.isMixedInvestmentAmount) && this.isWholeCDAvailable;
    },
    isDecreaseOpportunity() {
      return this.isMixedInvestmentAmount && this.isWholeCDAvailable;
    },
    isFractionalToWholeOpportunity() {
      return this.isFractionalInvestmentAmount && this.isFractionalCDAvailable && this.isWholeRateBetter;
    },
    isTopCardFractionalCD() {
      return (
        this.topCard.style !== ScreenerCardStyle.UNAVAILABLE_CARD && this.topCard.cusip === this.fractionalCD?.cusip
      );
    },
    isTopCardWholeCD() {
      return (
        this.topCard.style !== ScreenerCardStyle.UNAVAILABLE_CARD && this.topCard.cusip !== this.fractionalCD?.cusip
      );
    },

    // calculations
    fractionalPortion() {
      return this.investmentAmount % 1000;
    },
    wholePortion() {
      return Math.trunc(this.investmentAmount / 1000) * 1000;
    },
    mixedReturnAmount() {
      const wholeReturn = this.mixedWholeCard.returnAmount ?? 0;
      const fractionalReturn = this.mixedFractionalCard.returnAmount ?? 0;
      return wholeReturn + fractionalReturn;
    },
    mixedReturnAdvantage() {
      const allFractionalReturn = this.topCard.returnAmount ?? 0;
      return this.mixedReturnAmount - allFractionalReturn;
    },

    // card section strings
    cardSectionTitle() {
      if (!this.isOptionAvailable) {
        return this.clientConstants.SCREENER_NO_CDS;
      }

      let text = this.clientConstants.SCREENER_ONE_OPTION;
      if (this.isMixedOpportunity) {
        text = this.clientConstants.SCREENER_TWO_OPTIONS;
      }

      const formattedInvestmentAmount = this.formatDollarAmount(Number(this.investmentAmount), 0);
      text = text.replace('{{ amount }}', formattedInvestmentAmount);
      text = text.replace('{{ duration }}', this.currentDuration);
      return text;
    },
    option1Text() {
      if (!this.isOptionAvailable) {
        return '';
      }
      if (this.isFractionalInvestmentAmount) {
        return this.clientConstants.SCREENER_OPTION_FRACTIONAL;
      }
      if (this.isWholeInvestmentAmount) {
        return this.clientConstants.SCREENER_OPTION_WHOLE;
      }
      // mixed amount, both CDs available, whole amount is better than fractional
      if (this.isMixedOpportunity) {
        return this.clientConstants.SCREENER_OPTION1;
      }
      // mixed amount, but mixed CDs either not available or not optimal
      return this.clientConstants.SCREENER_OPTION_FRACTIONAL;
    },
    option2Text() {
      return this.clientConstants.SCREENER_OPTION2;
    },

    // Considerations
    considerations() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_TITLE;
    },
    totalReturn() {
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        this.topCard.returnAmount
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      const text = this.clientConstants.SCREENER_CONSIDERATIONS_RETURN.replace('{{ return }}', replacement);
      return this.isTopCardFractionalCD ? text : text + '<sup>*</sup>';
    },
    oneTransaction() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_ONE_TRANSACTION;
    },
    oneDate() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_ONE_DATE.replace(
        '{{ date }}',
        this.formatDate(this.topCard.maturityDate)
      );
    },
    combinedReturn() {
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        this.mixedReturnAmount
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_CONSIDERATIONS_COMBINED_RETURN.replace('{{ return }}', replacement);
    },
    earnMore() {
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        this.mixedReturnAdvantage
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_CONSIDERATIONS_EARN_MORE.replace('{{ advantage }}', replacement);
    },
    twoTransactions() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_TWO_TRANSACTIONS;
    },
    bothDates() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_BOTH_MATURITY_DATE.replace(
        '{{ date }}',
        this.formatDate(this.mixedWholeCard.maturityDate)
      );
    },
    fractionalDate() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_FRACTIONAL_MATURITY_DATE.replace(
        '{{ date }}',
        this.formatDate(this.mixedFractionalCard.maturityDate)
      );
    },
    wholeDate() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_WHOLE_MATURITY_DATE.replace(
        '{{ date }}',
        this.formatDate(this.mixedWholeCard.maturityDate)
      );
    },
    mayBeCallable() {
      return this.clientConstants.SCREENER_CONSIDERATIONS_MAY_BE_CALLABLE;
    },

    // Search tips
    searchTips() {
      return this.clientConstants.SCREENER_SEARCH_TIPS_TITLE;
    },
    increaseInvestmentTip() {
      const increase = 1000 - this.fractionalPortion;
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        increase,
        0
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_SEARCH_TIPS_INCREASE.replace('{{ amount }}', replacement);
    },
    decreaseInvestmentTip() {
      const decrease = this.fractionalPortion;
      const replacement = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        decrease,
        0
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_SEARCH_TIPS_DECREASE.replace('{{ amount }}', replacement);
    },
    fractionalToWholeTip() {
      const increase = 1000 - this.fractionalPortion;
      const apy = (this.wholeCD?.rate ?? 0).toFixed(2) + '%';
      const replacement1 = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${this.formatDollarAmount(
        increase,
        0
      )}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      const replacement2 = `${this.clientConstants.SCREENER_GREEN_SPAN_OPEN}${apy}${this.clientConstants.SCREENER_GREEN_SPAN_CLOSE}`;
      return this.clientConstants.SCREENER_SEARCH_TIPS_FRACTIONAL_TO_WHOLE.replace(
        '{{ amount }}',
        replacement1
      ).replace('{{ apy }}', replacement2);
    },
    changeDurationTip() {
      return this.clientConstants.SCREENER_SEARCH_TIPS_DURATION;
    },
    contentDetailList() {
      return store.contentItems[0]?.components.map((comp: ContentComponentsModel) => comp.contentDetail) ?? [];
    }
  },
  created() {
    if (this.$route !== undefined && this.$route.query !== undefined) {
      if ('amount' in this.$route.query) {
        this.investmentAmount = Number(this.$route.query.amount);
        this.amountValueInvalid = this.checkAmountInvalid(this.investmentAmount);
        this.amountMaxExceeded = Utils.formatNumber(this.investmentAmount) > 9999900;
      }
      if ('duration' in this.$route.query) {
        this.durationIndex = Number(this.$route.query.duration);
      }
    }
  },
  data: () => {
    return {
      linkList: [
        { text: ClientConstants.HOME, href: `${app?.hostUrl}`, id: 'gs-breadcrumb-home' },
        { text: ClientConstants.BREADCRUMB_ARROWS },
        {
          text: ClientConstants.RESEARCH,
          href: `${app?.hostUrl}${app?.fiBondsOverviewPath}`,
          id: 'gs-breadcrumb-fi-bonds'
        },
        { text: ClientConstants.BREADCRUMB_ARROWS },
        { text: ClientConstants.FRACTIONAL_CDS, href: `${app?.educationPagePath}`, id: 'gs-breadcrumb-edu' }
      ],
      amountValueInvalid: false,
      amountMaxExceeded: false,
      clientConstants: ClientConstants,
      openContactUs: "window.open('" + ClientConstants.CONTACT_US_URL + "', '_blank');",
      store: reactive(store),
      investmentAmount: 0,
      durationIndex: 0,
      fiCommonRiskUrl: app ? `${app.hostUrl}${app.fiCommonRiskPath}` : '',
      cdSearchURL: app ? `${app.fixedIncomeHost}${app.cdSearchPath}` : '',
      contactUsUrl: app ? `${app.hostUrl}${app.contactUsPath}` : '',
      fullOfferingsUrl: app ? `${app.fixedIncomeHost}${app.fiLandingCdLadders}` : '',
      animationTrigger: 0,
      windowWidth: window.innerWidth,
      disclosures: [] as ContentDisclosuresModel[]
    };
  },
  methods: {
    onResize: function () {
      this.windowWidth = window.innerWidth;
    },
    checkAmountInvalid(amount) {
      const passedNumber = Utils.formatNumber(amount);
      return passedNumber == undefined || isNaN(passedNumber) || passedNumber < 100 || passedNumber % 100 != 0;
    },
    amountChange(event) {
      this.amountValueInvalid = this.checkAmountInvalid(event.target.value);
      this.amountMaxExceeded = Utils.formatNumber(event.target.value) > 9999900;
      if (!this.amountValueInvalid && !this.amountMaxExceeded) {
        this.investmentAmount = Utils.formatNumber(event.target.value);
        this.animationTrigger++;
      }
    },
    durationChange(event) {
      if (!this.amountValueInvalid && !this.amountMaxExceeded) {
        this.animationTrigger++;
        this.durationIndex = Number(event.target.value);
      }
    },
    openModal() {
      (this.$refs.fdicModal as typeof FdicModal).openFdicModal();
    },
    openScreenerModal() {
      (this.$refs.screenerModal as typeof ScreenerModal).openScreenerModal();
    },
    cardInfo(index: number, cardAmount: number): CardInfo {
      // this should never happen
      if (index < 0 || index >= this.offerings.length) {
        return { style: ScreenerCardStyle.UNAVAILABLE_CARD, duration: undefined, title: undefined };
      }

      const cd = this.chooseCD(cardAmount, this.offerings[index].fractionalCD, this.offerings[index].wholeCD);
      const cardAmountIsFractional = cardAmount % 1000 != 0;

      // unavailable card if no CD matches the selected amount
      if (cd === undefined) {
        const duration = Object.values(Duration)[index];
        let titleText = '';

        if (!this.isWholeCDAvailable && !this.isFractionalCDAvailable) {
          titleText = this.clientConstants.SCREENER_CARD_NO_CDS;
        } else if (cardAmountIsFractional) {
          titleText = this.clientConstants.SCREENER_CARD_NO_FRACTIONAL_CDS;
        }

        const title = titleText.replace('{{ duration }}', duration);
        const searchURL = `${this.cdSearchURL}${this.clientConstants.ALL_CDS_QUERY}`;
        return { style: ScreenerCardStyle.UNAVAILABLE_CARD, duration, title, searchURL };
      }

      // buy card
      const style = ScreenerCardStyle.BUY_CARD;
      const cusip = cd.cusip;
      const duration = Object.values(Duration)[index];
      const apy = cd.rate.toFixed(2) + '%';
      const returnAmount = Utils.calculateReturnAmount(cardAmount, cd.rate, cd.datedDate, cd.maturityDate);
      const formattedCardAmount = this.formatDollarAmount(cardAmount, 0);
      const title = cardAmountIsFractional
        ? this.clientConstants.SCREENER_CARD_FRACTIONAL_AMOUNT.replace('{{ amount }}', formattedCardAmount)
        : this.clientConstants.SCREENER_CARD_WHOLE_AMOUNT.replace('{{ amount }}', formattedCardAmount);
      const maturityDate = cd.maturityDate;
      const searchURL = cardAmountIsFractional
        ? Utils.buildFractionalCDSearchURL(this.cdSearchURL, cusip)
        : Utils.buildWholeCDSearchURL(
            this.cdSearchURL,
            this.offerings[index].minMaturityDate as Date,
            this.offerings[index].maxMaturityDate as Date
          );
      return { style, cusip, duration, apy, returnAmount, title, maturityDate, searchURL };
    },
    chooseCD(amount: number, fractionalCD?: CDDetail, wholeCD?: CDDetail): CDDetail | undefined {
      let selectedCD = fractionalCD;
      const fractionalRate = fractionalCD?.rate;
      const wholeRate = wholeCD?.rate;
      const isWholeAmount = amount > 0 && amount % 1000 == 0;
      if (isWholeAmount && wholeRate !== undefined && (fractionalRate === undefined || fractionalRate <= wholeRate)) {
        selectedCD = wholeCD;
      }
      return selectedCD;
    },
    formatDollarAmount(amount?: number, decimalPlaces = 2): string {
      if (!amount || typeof amount !== 'number') {
        return '$0';
      }
      return (
        '$' +
        Number(amount.toFixed(decimalPlaces)).toLocaleString('en-US', {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        })
      );
    },
    formatDate(dateString: string | undefined) {
      return Utils.formatMaturityDate(dateString);
    }
  }
});
